import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { BigNumber } from '@ethersproject/bignumber';
import type { CardProps } from '@ui/components/Card';
import { Card } from '@ui/components/Card';
import Features from '@ui/components/Features';
import { TokenIcon } from '@ui/components/TokenIcon';
import type { CoinMode } from '@ui/hooks/useCoinMode';
import { useCoinUsdValue } from '@ui/hooks/useCoinUsdValue';
import { useVnoUsdValue } from '@ui/hooks/useVnoUsdValue';
import { useTranslations } from '@ui/i18n';
import { formatTokenAmount } from '@ui/utils/format';
import type { GardenType } from '@ui/utils/garden';
import React, { useMemo } from 'react';

import {
  useHarvestAllDrawer,
  useHarvestAllRewardsInfo,
} from './HarvestAllDrawer/useHarvestAllDrawer';

type HarvestAllActionCardProps = CardProps;

function HarvestAllActionCard({ ...cardProps }: HarvestAllActionCardProps) {
  const t = useTranslations();
  const openHarvestAll = useHarvestAllDrawer((s) => s.onOpen);
  const {
    gardensHarvestable,
    fountainHarvestable,
    reservoirHarvestable,
    pendingRewardsFountain,
    pendingRewardsReservoir,
    pendingRewardsMap,
  } = useHarvestAllRewardsInfo();
  const harvestable = useMemo(
    () =>
      !!gardensHarvestable.length ||
      fountainHarvestable ||
      reservoirHarvestable,
    [fountainHarvestable, gardensHarvestable.length, reservoirHarvestable],
  );

  const harvestableVnoTotal = useMemo(() => {
    let total = BigNumber.from(0);

    for (const coinModeKey in pendingRewardsMap) {
      const coinModeValues = pendingRewardsMap[coinModeKey as CoinMode];

      for (const gardenTypeKey in coinModeValues) {
        const value = coinModeValues[gardenTypeKey as GardenType];

        total = value.usd?.gt(BigNumber.from(10).pow(18).mul(2))
          ? total.add(value.data ?? BigNumber.from(0))
          : total;
      }
    }
    return total.add(
      fountainHarvestable && pendingRewardsFountain
        ? pendingRewardsFountain
        : BigNumber.from(0),
    );
  }, [fountainHarvestable, pendingRewardsFountain, pendingRewardsMap]);

  const croRewardsUsd = useCoinUsdValue(
    pendingRewardsReservoir.wcro,
    'cro',
  ).data;
  const atomRewardsUsd = useCoinUsdValue(
    pendingRewardsReservoir.atom,
    'atom',
  ).data;
  const vnoRewardsUsd = useVnoUsdValue(harvestableVnoTotal).data;
  const totalRewardsUsd = (
    reservoirHarvestable && croRewardsUsd ? croRewardsUsd : BigNumber.from(0)
  )
    .add(vnoRewardsUsd ?? BigNumber.from(0))
    .add(
      reservoirHarvestable && atomRewardsUsd
        ? atomRewardsUsd
        : BigNumber.from(0),
    );

  return (
    <Features.HarvestAll>
      {harvestable && (
        <Card
          m={0}
          p={2.5}
          justifyContent="space-between"
          display="flex"
          {...cardProps}
        >
          <Flex flexDir="column" justify="space-between" gap={2}>
            <Text textStyle={{ base: 'body', desktop: 'h4' }}>
              {t('Unclaimed rewards')}
            </Text>
            <Button onClick={openHarvestAll}>{t('Harvest all')}</Button>
          </Flex>
          <Flex flexDir="column" textAlign="right" gap={1}>
            <Box
              textStyle={{ base: 'bodyBold', desktop: 'h4Bold' }}
              wordBreak="break-all"
            >
              {formatTokenAmount(totalRewardsUsd, 2)} USD
            </Box>
            {reservoirHarvestable && (
              <>
                <Flex
                  align="center"
                  justify="flex-end"
                  color="text.light"
                  textStyle={{ base: 'bodySmall', desktop: 'body' }}
                >
                  {formatTokenAmount(pendingRewardsReservoir.wcro, 4)}
                  <TokenIcon token="cro" />
                </Flex>
                <Flex
                  align="center"
                  justify="flex-end"
                  color="text.light"
                  textStyle={{ base: 'bodySmall', desktop: 'body' }}
                >
                  {formatTokenAmount(pendingRewardsReservoir.atom, 4, 6)}
                  <TokenIcon token="atom" />
                </Flex>
              </>
            )}
            {harvestableVnoTotal.gt(0) && (
              <Flex
                align="center"
                justify="flex-end"
                color="text.light"
                textStyle={{ base: 'bodySmall', desktop: 'body' }}
              >
                {formatTokenAmount(harvestableVnoTotal, 4)}
                <TokenIcon token="vno" />
              </Flex>
            )}
          </Flex>
        </Card>
      )}
    </Features.HarvestAll>
  );
}

export default HarvestAllActionCard;
